<template>
  <!-- TL-01: Live region wrappers are always in the DOM so AT registers them before content is injected.
       Two separate regions are needed because aria-live="assertive" (errors) vs "polite" (success)
       must be established at page load, not at the moment the toast fires. -->
  <div role="status" aria-live="polite" aria-atomic="true">
    <div
      v-if="isShow && type !== 'error' && placement === 'center'"
      class="toast-message ym-hide-content"
    >
      <div>{{ text }}</div>
      <SvgoCheck aria-hidden="true" />
    </div>

    <div
      v-if="isShow && type !== 'error' && placement === 'bottom-left'"
      class="toast-message toast-message--bottom-left ym-hide-content"
    >
      <SvgoCheckCircle class="toast-message__icon" aria-hidden="true" />
      <div class="toast-message__content">
        <p v-if="title" class="toast-message__title">{{ title }}</p>
        <p v-if="subtitle" class="toast-message__subtitle">{{ subtitle }}</p>
      </div>
      <button
        type="button"
        class="toast-message__close"
        aria-label="Закрыть уведомление"
        @click="dismiss"
      >
        <SvgoClose aria-hidden="true" />
      </button>
    </div>
  </div>
  <div role="alert" aria-live="assertive" aria-atomic="true">
    <div
      v-if="isShow && type === 'error'"
      class="toast-message toast-message--error ym-hide-content"
      :class="{ 'toast-message--bottom-left': placement === 'bottom-left' }"
    >
      <div>{{ text }}</div>
      <SvgoCheck aria-hidden="true" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { TToastDetail, TToastPlacement } from '~/composables/useToast';

const text = ref('');
const title = ref('');
const subtitle = ref('');
const type = ref<'success' | 'error'>('success');
const placement = ref<TToastPlacement>('center');
const isShow = ref(false);
let toastTimer: ReturnType<typeof setTimeout> | null = null;

function resolveToastText(detail: TToastDetail): string {
  const resolvedTitle = detail.title?.trim() || detail.text?.trim() || '';
  const resolvedSubtitle = detail.subtitle?.trim();

  if (!resolvedTitle) {
    return '';
  }

  return resolvedSubtitle ? `${resolvedTitle}. ${resolvedSubtitle}` : resolvedTitle;
}

function clearToastTimer() {
  if (toastTimer !== null) {
    clearTimeout(toastTimer);
    toastTimer = null;
  }
}

function dismiss() {
  clearToastTimer();
  isShow.value = false;
}

function scheduleDismiss() {
  clearToastTimer();
  const delay = type.value === 'error' ? 10000 : 6000;
  toastTimer = setTimeout(() => {
    isShow.value = false;
    toastTimer = null;
  }, delay);
}

onMounted(() => {
  document.addEventListener('show-toast', toastHandler);
});

onUnmounted(() => {
  document.removeEventListener('show-toast', toastHandler);
  clearToastTimer();
});

const toastHandler = async (event: Event) => {
  const detail = (event as CustomEvent<TToastDetail>).detail;

  clearToastTimer();

  // TL-01: If already showing, briefly hide so the live region DOM change triggers a new AT announcement.
  if (isShow.value) {
    isShow.value = false;
    await nextTick();
  }

  title.value = detail.title?.trim() || detail.text?.trim() || '';
  subtitle.value = detail.subtitle?.trim() || '';
  text.value = resolveToastText(detail);
  type.value = detail.type ?? 'success';
  placement.value = detail.placement ?? 'center';
  isShow.value = placement.value === 'bottom-left'
    ? !!title.value
    : !!text.value;

  if (!isShow.value) {
    return;
  }

  scheduleDismiss();
};
</script>

<style lang="scss">
.toast-message {
  position: fixed;
  bottom: 35px;
  z-index: 9999;
  left: 50%;
  transform: translateX(-50%);
  background-color: $color-white;
  color: $color-accent-text;
  border-radius: 16px;
  box-shadow: 0 4px 40px 0 #8A8AB033;
  padding: 16px 24px;
  font-size: 18px;
  line-height: 1.1;
  width: max-content;
  max-width: 95%;
  text-align: center;
  opacity: 1;
  animation: fade-out 1s ease-in-out forwards;
  animation-delay: 5s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  svg {
    margin-left: 8px;
    font-size: 20px;
  }

  &--error {
    background-color: $color-danger-bg;
    color: tint($color-danger-text, $color-black, 45%);
    animation-delay: 9s;

    svg {
      display: none;
    }
  }

  &--bottom-left {
    position: fixed;
    left: 24px;
    bottom: 24px;
    transform: none;
    width: auto;
    max-width: min(420px, calc(100vw - 48px));
    padding: 16px 40px 16px 16px;
    text-align: left;
    background-color: $color-success;
    color: $color-black;
    box-shadow: 0 4px 24px 0 rgba(138, 138, 176, 0.16);
    animation: none;
    align-items: flex-start;
    gap: 12px;

    svg {
      margin-left: 0;
    }

    &.toast-message--error {
      background-color: $color-danger-bg;
      color: tint($color-danger-text, $color-black, 45%);
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    color: $color-black;
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.35;
  }

  &__subtitle {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.35;
    color: $color-gray;
  }

  &__close {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    background: none;
    color: $color-gray;
    cursor: pointer;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  @media screen and (max-width: 600px) {
    font-size: 14px;
    padding: 10px 15px;
    border-radius: 10px;
    box-shadow: 0 4px 9px 0 #8A8AB033;

    svg {
      font-size: 16px;
      margin-left: 4px;
    }

    &--bottom-left {
      left: 16px;
      bottom: 16px;
      max-width: calc(100vw - 32px);
      padding: 14px 36px 14px 14px;
      border-radius: 12px;

      .toast-message__title {
        font-size: 14px;
      }

      .toast-message__subtitle {
        font-size: 13px;
      }
    }
  }
}

@keyframes fade-out {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}
</style>
